import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import type { IPortfolioCodeQualityAggregate } from '@skystone/shared';

interface MedianCoverageTileProps {
  aggregate: IPortfolioCodeQualityAggregate;
  inScopeRepos: number;
}

/**
 * Verdict-strip tile #2 — Median overall coverage across in-scope repos.
 *
 * Per D10, when fewer than 3 repos are in scope the value falls back from a
 * true median to the raw value (n=1) or simple average (n=2). The tile copy
 * reflects that — "Median across N repos" vs "Coverage of N repo(s)" — so
 * the user knows what they're looking at.
 *
 * No delta in v1 (bd skystone-pk3b).
 */
export function MedianCoverageTile({
  aggregate,
  inScopeRepos,
}: Readonly<MedianCoverageTileProps>) {
  const { medianCoverage } = aggregate;
  const valueText = medianCoverage === null ? '—' : `${medianCoverage.toFixed(1)}%`;
  const sublabel = computeSublabel(medianCoverage, inScopeRepos);

  return (
    <Card className="shadow-none" data-testid="cq-median-coverage-tile">
      <CardContent className="p-4">
        <div className="flex items-center gap-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Coverage
          </p>
          <MetricInfoPopover metricKey="cq_coverage" />
        </div>
        <p className="mt-2 text-2xl font-bold leading-none text-foreground">{valueText}</p>
        <p className="mt-1 text-xs text-muted-foreground">{sublabel}</p>
      </CardContent>
    </Card>
  );
}

function computeSublabel(median: number | null, inScopeRepos: number): string {
  if (median === null) return inScopeRepos === 0 ? 'No repos in scope' : 'No coverage data';
  if (inScopeRepos === 1) return 'Coverage of 1 repo';
  if (inScopeRepos === 2) return 'Average across 2 repos';
  return `Median across ${inScopeRepos} repos`;
}
